import type { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useCurrentProfile } from '@/hooks/useCurrentProfile';

// Guard for invited members who haven't set their name yet.
//
// When an invited member accepts their invitation they only set a password —
// firstName and lastName come from their profile form in Settings. Until they
// fill those in, every protected route redirects them to /settings so they
// complete their profile before using the app.
//
// Skipped for:
//   - SuperAdmins (platform-level, no member profile required)
//   - Platform-only users with no tenant membership (tenantId null)
//   - The /settings path itself (prevents an infinite redirect loop)
//   - While the profile is still loading (avoids a flash redirect)
export function ProfileCompletionGuard({ children }: PropsWithChildren) {
  const location = useLocation();
  const { data: profile, isLoading } = useCurrentProfile();

  // Let the settings page through unconditionally — it's where we redirect to.
  if (location.pathname.startsWith('/settings')) {
    return <>{children}</>;
  }

  // Still fetching — render children optimistically (OnboardingCompletionGuard
  // already blocked unauthenticated access; a brief flash here is acceptable).
  if (isLoading || !profile) {
    return <>{children}</>;
  }

  // SuperAdmins and platform-only users don't need a member profile.
  if (profile.isSuperAdmin || !profile.tenantId) {
    return <>{children}</>;
  }

  // Tenant member with no name — send to profile settings.
  if (!profile.firstName && !profile.lastName) {
    return <Navigate to="/settings" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
